import { useEffect, useId, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Avatar, cn, SkipLink, VisuallyHidden } from '@ircf/ui';
import type { RouteDefinition } from '@ircf/config';
import { brand } from '@ircf/config';
import { meApi } from '../lib/api/endpoints.js';
import { queryKeys } from '../lib/api/queryKeys.js';
import { useContent } from '../app/content.js';
import { useSession } from '../app/session.js';
import { initialsOf } from '../lib/format.js';
import { InstallPrompt } from '../components/pwa/InstallPrompt.js';
import { MessageFab } from '../components/portal/MessageFab.js';
import { PortalBottomNav, primaryRouteIds } from '../components/portal/PortalBottomNav.js';

/**
 * Operational shell shared by the client portal and the trainer workspace.
 *
 * Desktop: sidebar. Phone: bottom nav plus a More sheet for the rest.
 */

export type BadgeKey = 'messages' | 'notifications' | 'checkIns' | 'feedback' | 'bookings';

export interface AppShellProps {
  /** Navigation entries, from `portalNav` or `adminNav`. */
  nav: readonly RouteDefinition[];
  /** Area name shown beneath the wordmark, e.g. `Client Portal`. */
  areaLabel: string;
  /** Maps a route id suffix to the unread count that should badge it. */
  badges?: Partial<Record<string, number>>;
  /** Where the notifications route lives for this area. */
  notificationsPath: string;
  /** Where the profile/settings route lives for this area. */
  settingsPath: string;
  variant: 'client' | 'trainer';
  /** Registration waiting room: header and logout only. */
  locked?: boolean;
}

export function AppShell({
  nav,
  areaLabel,
  badges = {},
  notificationsPath,
  settingsPath,
  variant,
  locked = false,
}: AppShellProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const content = useContent();
  const { session, logout } = useSession();
  const [navOpen, setNavOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const navId = useId();
  const userMenuId = useId();
  const userMenuRef = useRef<HTMLDivElement>(null);

  /**
   * Close both overlays on navigation.
   *
   * Adjusted during render rather than in an effect. An effect would paint the
   * new route with the old menu still open for one frame, and on a phone that
   * frame is the whole screen. React discards this render and re-runs before
   * committing, so nothing flashes.
   */
  const [renderedPath, setRenderedPath] = useState(location.pathname);
  if (renderedPath !== location.pathname) {
    setRenderedPath(location.pathname);
    setNavOpen(false);
    setUserMenuOpen(false);
  }

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (navOpen) {
        setNavOpen(false);
      }
      setUserMenuOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [navOpen]);

  useEffect(() => {
    if (!userMenuOpen) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!userMenuRef.current?.contains(event.target as Node)) setUserMenuOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [userMenuOpen]);

  const displayName =
    session?.client?.preferredName ?? session?.user.displayName ?? session?.user.email ?? '';
  const businessName = content.business.name;

  const totalNotifications = badges.notifications ?? 0;
  const moreIds = new Set(primaryRouteIds(variant));
  const moreNav = nav.filter((route) => !moreIds.has(route.id));

  async function handleLogout(): Promise<void> {
    await logout();
    await navigate('/login', { replace: true });
  }

  return (
    <div
      data-shell="portal"
      className={cn(
        'bg-portal-atmosphere min-h-dvh w-full max-w-full overflow-x-clip',
        !locked && 'lg:grid lg:grid-cols-[17.5rem_1fr]',
      )}
    >
      <SkipLink href="#main">Skip to main content</SkipLink>

      {!locked ? (
      <aside className="bg-portal-sidebar hidden border-r border-white/10 lg:flex lg:flex-col">
        <Link
          to="/"
          className="flex items-center gap-3 border-b border-white/10 px-5 py-6 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-warm-white"
        >
          <img
            src={content.business.logoUrl}
            alt=""
            width={40}
            height={40}
            className="size-10 shrink-0 object-contain"
          />
          <span className="flex min-w-0 flex-col leading-tight">
            <span className="font-condensed truncate text-base font-semibold tracking-wide text-warm-white">
              {businessName}
            </span>
            <span className="mt-0.5 text-[0.65rem] font-medium tracking-[0.16em] text-platinum uppercase">
              {brand.masterBrandLine}
            </span>
            <span className="mt-0.5 text-[0.65rem] font-medium tracking-[0.18em] text-accent-pt-bright uppercase">
              {areaLabel}
            </span>
          </span>
        </Link>

        <SidebarNav nav={nav} badges={badges} className="flex-1 overflow-y-auto px-3 py-5" />

        <div className="border-t border-white/8 px-4 py-4">
          <p className="text-[0.65rem] leading-relaxed tracking-wide text-cool-steel">
            Built for coaching - fast, clear, on your phone between sets.
          </p>
        </div>
      </aside>
      ) : null}

      <div className="flex min-h-dvh min-w-0 flex-col">
        <header
          className="sticky top-0 z-30 border-b border-white/10 bg-near-black/70 backdrop-blur-xl"
          data-print-hide="true"
        >
          <div className="flex h-14 items-center gap-2 px-3 sm:h-16 sm:px-6">
            <img
              src={content.business.logoUrl}
              alt=""
              width={32}
              height={32}
              className="size-8 shrink-0 object-contain lg:hidden"
            />
            <span className="font-condensed truncate text-base font-semibold tracking-wide text-warm-white lg:hidden">
              {areaLabel}
            </span>

            <div className="ml-auto flex items-center gap-1 sm:gap-2">
              {!locked ? (
              <Link
                to={notificationsPath}
                className="relative flex size-10 items-center justify-center rounded-xl text-platinum transition-colors hover:bg-accent-pt/15 hover:text-accent-pt-bright focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-warm-white"
              >
                <VisuallyHidden>
                  Notifications
                  {totalNotifications > 0 ? `, ${totalNotifications} unread` : ', none unread'}
                </VisuallyHidden>
                <svg aria-hidden="true" viewBox="0 0 24 24" className="size-5" fill="none">
                  <path
                    d="M12 4a5 5 0 0 0-5 5v3.5L5.5 15h13L17 12.5V9a5 5 0 0 0-5-5Zm0 15.5a2 2 0 0 0 2-2h-4a2 2 0 0 0 2 2Z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinejoin="round"
                  />
                </svg>
                {totalNotifications > 0 ? (
                  <span
                    aria-hidden="true"
                    className="absolute top-1.5 right-1.5 flex min-w-4 items-center justify-center rounded-full bg-accent-pt px-1 text-[0.625rem] font-semibold text-white shadow-[0_0_12px_var(--color-accent-pt)]"
                  >
                    {totalNotifications > 9 ? '9+' : totalNotifications}
                  </span>
                ) : null}
              </Link>
              ) : null}

              <div ref={userMenuRef} className="relative">
                <button
                  type="button"
                  className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] py-1.5 pr-2.5 pl-1.5 text-sm text-platinum transition-colors hover:border-accent-pt/30 hover:bg-accent-pt/10 hover:text-warm-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-warm-white"
                  aria-expanded={userMenuOpen}
                  aria-controls={userMenuId}
                  aria-haspopup="menu"
                  onClick={() => setUserMenuOpen((open) => !open)}
                >
                  <Avatar initials={initialsOf(displayName)} size="sm" />
                  <span className="hidden max-w-32 truncate font-medium sm:inline">{displayName}</span>
                  <svg aria-hidden="true" viewBox="0 0 20 20" className="size-4" fill="none">
                    <path
                      d="M6 8l4 4 4-4"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>

                {userMenuOpen ? (
                  <div
                    id={userMenuId}
                    role="menu"
                    className="absolute right-0 z-40 mt-2 w-56 overflow-hidden rounded-2xl border border-white/10 bg-graphite/95 shadow-2xl backdrop-blur-xl"
                  >
                    <div className="border-b border-white/10 bg-gradient-to-r from-accent-pt/15 to-transparent px-4 py-3">
                      <p className="truncate text-sm font-medium text-warm-white">{displayName}</p>
                      <p className="truncate text-xs text-cool-steel">{session?.user.email}</p>
                    </div>
                    {!locked ? (
                    <>
                    <Link
                      to={settingsPath}
                      role="menuitem"
                      className="block px-4 py-2.5 text-sm text-platinum hover:bg-white/5 hover:text-warm-white"
                    >
                      Settings
                    </Link>
                    <Link
                      to="/"
                      role="menuitem"
                      className="block px-4 py-2.5 text-sm text-platinum hover:bg-white/5 hover:text-warm-white"
                    >
                      View public site
                    </Link>
                    </>
                    ) : null}
                    <button
                      type="button"
                      role="menuitem"
                      onClick={handleLogout}
                      className="block w-full border-t border-white/10 px-4 py-2.5 text-left text-sm text-platinum hover:bg-white/5 hover:text-warm-white"
                    >
                      Log out
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          {navOpen ? (
            <div
              id={navId}
              className="bg-portal-sidebar max-h-[70vh] overflow-y-auto border-t border-white/10 lg:hidden"
            >
              <SidebarNav nav={moreNav} badges={badges} className="p-3" />
            </div>
          ) : null}
        </header>

        <main
          id="main"
          tabIndex={-1}
          className={cn(
            'min-w-0 flex-1 focus-visible:outline-none',
            locked
              ? 'pb-8'
              : 'pb-[calc(5.5rem+env(safe-area-inset-bottom,0px))] lg:pb-0',
          )}
        >
          <Outlet />
        </main>

        {!locked ? (
          <>
        <PortalBottomNav
          variant={variant}
          nav={nav}
          badges={badges}
          moreOpen={navOpen}
          onToggleMore={() => setNavOpen((open) => !open)}
          moreId={navId}
        />
        {variant === 'client' ? <MessageFab unread={badges.messages ?? 0} /> : null}
          </>
        ) : null}
        <InstallPrompt className="bottom-[calc(5.75rem+env(safe-area-inset-bottom,0px))] lg:bottom-4" />
      </div>
    </div>
  );
}

function SidebarNav({
  nav,
  badges,
  className,
}: {
  nav: readonly RouteDefinition[];
  badges: Partial<Record<string, number>>;
  className?: string;
}) {
  return (
    <nav aria-label="Portal" className={className}>
      <ul className="space-y-1">
        {nav.map((route) => {
          const badgeKey = route.id.replace(/^(portal|admin)-/, '');
          const count = badges[badgeKey] ?? 0;

          return (
            <li key={route.id}>
              <NavLink
                to={route.path}
                className={({ isActive }) =>
                  cn(
                    'flex min-h-11 items-center justify-between gap-3 rounded-xl px-3.5 text-sm font-medium transition-[background-color,color,box-shadow,transform] duration-200',
                    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-warm-white',
                    isActive
                      ? 'border border-accent-pt/50 bg-accent-pt/20 text-warm-white shadow-[0_10px_28px_-10px_var(--color-accent-pt)]'
                      : 'text-platinum hover:bg-white/[0.06] hover:text-warm-white',
                  )
                }
              >
                <span className="tracking-wide">{route.label}</span>
                {count > 0 ? (
                  <span
                    className={cn(
                      'flex min-w-5 items-center justify-center rounded-full px-1.5 text-xs font-semibold',
                      'bg-white/20 text-white',
                    )}
                  >
                    {count > 99 ? '99+' : count}
                    <VisuallyHidden> unread</VisuallyHidden>
                  </span>
                ) : null}
              </NavLink>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

/** Shared unread-count query, so both portals badge navigation from one request. */
export function useUnreadCounts() {
  return useQuery({
    queryKey: queryKeys.me.unreadCounts,
    queryFn: meApi.unreadCounts,
    staleTime: 60_000,
    retry: 1,
  });
}
